// Aesthetics & Medical Cosmetology treatments offered at Healic.
// Used by /aesthetics and /aesthetics/$slug.

export type AestheticStep = { title: string; points: string[] };

export type AestheticTreatment = {
  slug: string;
  name: string;
  tagline: string;
  short: string;
  overview: string;
  sessions: string;
  duration: string;
  downtime: string;
  steps: AestheticStep[];
  benefits: string[];
  causes: string[];
  areas?: string[];
  aftercare: string[];
  faqs: { q: string; a: string }[];
};

export const aestheticTreatments: AestheticTreatment[] = [
  {
    slug: "laser-hair-reduction",
    name: "Laser Hair Reduction (LHR)",
    tagline: "Long-term freedom from shaving, waxing and threading",
    short:
      "Laser hair reduction uses concentrated light energy to target and destroy hair follicles, reducing hair growth over time.",
    overview:
      "Laser hair reduction is a cosmetic procedure that uses concentrated light (laser energy) to target and destroy hair follicles, reducing hair growth over time. It is minimally invasive, precise, and suitable for various areas of the body such as the face, legs, arms, underarms and bikini area. Unlike shaving or waxing, laser hair reduction provides long-term results by targeting the hair growth cycle.",
    sessions: "6–8 sessions, 4–6 weeks apart",
    duration: "5 minutes (small areas) to 1 hour (large areas)",
    downtime: "None — resume daily activities same day",
    steps: [
      {
        title: "Consultation and assessment",
        points: [
          "Skin type, hair colour and treatment area are evaluated.",
          "A patch test may be done to check skin reaction.",
        ],
      },
      {
        title: "Preparation",
        points: [
          "The area is cleaned and sometimes shaved.",
          "Protective eyewear is worn by both patient and practitioner.",
        ],
      },
      {
        title: "Laser application",
        points: [
          "The laser device delivers light pulses to the targeted area.",
          "Light is absorbed by melanin in the hair follicle, damaging it while leaving surrounding skin unharmed.",
          "Procedure duration varies from 5 minutes (small areas) to 1 hour (large areas).",
        ],
      },
      {
        title: "Post-procedure care",
        points: [
          "Cooling gel or ice packs may be applied.",
          "Avoid sun exposure and tanning for a few days.",
          "Multiple sessions (typically 6–8) are required for optimal results, spaced 4–6 weeks apart.",
        ],
      },
    ],
    benefits: [
      "Long-term hair reduction — reduces the need for shaving or waxing.",
      "Precision — targets dark, coarse hairs while sparing surrounding skin.",
      "Speed — small areas treated in minutes; larger areas in about an hour.",
      "Reduced ingrown hairs compared to shaving or waxing.",
      "Minimal side effects — usually temporary redness or mild swelling.",
    ],
    causes: [
      "Excess hair growth due to genetics or hormonal changes.",
      "Desire for long-term hair reduction instead of temporary methods.",
      "Works best on dark, coarse hair and lighter skin, as the laser targets melanin.",
      "Hormonal conditions like PCOS may cause excessive hair growth and can be managed with laser therapy in combination with medical treatment.",
    ],
    areas: [
      "Full face & upper lip",
      "Chin & jawline",
      "Underarms",
      "Arms & forearms",
      "Full legs",
      "Bikini & Brazilian",
      "Chest & abdomen",
      "Back & shoulders",
      "Full body packages",
    ],
    aftercare: [
      "Apply the prescribed soothing gel or moisturiser for 2–3 days.",
      "Use broad-spectrum SPF 30+ daily on treated areas.",
      "Avoid sun exposure, tanning beds, saunas and steam for 48–72 hours.",
      "Do not wax, thread or pluck between sessions — shaving is allowed.",
      "Avoid perfumed products, scrubs and hot showers for 24 hours.",
      "Return for each session on schedule to match the hair growth cycle.",
    ],
    faqs: [
      {
        q: "Is laser hair reduction permanent?",
        a: "It gives long-term hair reduction rather than complete permanent removal. After 6–8 sessions most people see 70–90% reduction, with occasional maintenance sessions once or twice a year.",
      },
      {
        q: "Is the procedure painful?",
        a: "Most patients describe a warm snapping sensation. Modern lasers use integrated cooling, and a numbing cream can be applied for sensitive areas such as the upper lip or bikini line.",
      },
      {
        q: "How many sessions will I need?",
        a: "Typically 6–8 sessions spaced 4–6 weeks apart, because only hair in the active growth phase responds to the laser. Hormonal cases such as PCOS may need a few extra sessions.",
      },
      {
        q: "Is it safe for Indian skin?",
        a: "Yes. We use lasers suited to Indian skin tones with adjustable settings and a patch test before your first full session.",
      },
      {
        q: "Can I have LHR if I have PCOS?",
        a: "Yes. Laser works well for PCOS-related facial and body hair, and results last longer when combined with medical management of the hormonal imbalance — which our gynaecology team can arrange.",
      },
      {
        q: "What should I avoid before a session?",
        a: "Avoid waxing, threading, plucking and bleaching for 3–4 weeks before treatment, and avoid sun exposure or tanning for at least a week.",
      },
    ],
  },
  {
    slug: "medi-facial",
    name: "Medi-Facial & Skin Brightening",
    tagline: "Dermatologist-grade cleansing, hydration and instant glow",
    short:
      "A medical-grade facial that deep-cleanses, exfoliates and hydrates the skin using serums selected for your skin concern.",
    overview:
      "A medi-facial is a dermatologist-supervised facial that combines deep cleansing, gentle exfoliation, extraction and infusion of active serums such as vitamin C, hyaluronic acid or niacinamide. Unlike a salon facial, the products and settings are chosen after a skin assessment, making it suitable for dull skin, tanning, mild pigmentation, open pores and pre-event glow.",
    sessions: "Single session; monthly for maintenance",
    duration: "45–60 minutes",
    downtime: "None",
    steps: [
      { title: "Skin assessment", points: ["Skin type, sensitivity and concerns are mapped.", "A serum protocol is selected for you."] },
      { title: "Cleanse & exfoliate", points: ["Double cleansing followed by gentle exfoliation.", "Painless extraction of blackheads and whiteheads."] },
      { title: "Active infusion", points: ["Serums are infused using ultrasound or micro-jet technology.", "A mask suited to your concern is applied."] },
      { title: "Finish & protect", points: ["Hydrating moisturiser and sunscreen are applied.", "Home-care routine is explained."] },
    ],
    benefits: [
      "Immediate brightness and hydration with no downtime.",
      "Reduces tanning, dullness and clogged pores.",
      "Customised actives for acne-prone, dry or sensitive skin.",
      "Safe to repeat monthly.",
    ],
    causes: ["Dull, tired or tanned skin", "Open pores and blackheads", "Dehydrated skin", "Pre-wedding or pre-event preparation"],
    aftercare: [
      "Use a gentle cleanser for 48 hours.",
      "Apply SPF 30+ every morning.",
      "Avoid scrubs, retinol and exfoliating acids for 3 days.",
    ],
    faqs: [
      { q: "How is this different from a salon facial?", a: "A medi-facial uses medical-grade actives and devices, chosen after a dermatologist assessment of your skin." },
      { q: "How soon will I see results?", a: "Skin looks brighter and more hydrated immediately; pigmentation and pore texture improve over 3–4 monthly sessions." },
      { q: "Is it safe for acne-prone skin?", a: "Yes — a salicylic or anti-acne protocol is used instead of a hydrating one." },
    ],
  },
  {
    slug: "chemical-peel",
    name: "Chemical Peels",
    tagline: "Targeted treatment for pigmentation, acne marks and dull skin",
    short:
      "Medical-grade peels resurface the top layer of skin to improve pigmentation, acne marks, tanning and uneven tone.",
    overview:
      "Chemical peels use controlled application of medical-grade acids — glycolic, salicylic, mandelic, lactic or a combination — to remove damaged outer layers of skin and stimulate healthy renewal. Peel strength is chosen according to your skin type and concern, making it effective for melasma, post-acne marks, tanning, dull skin and clogged pores.",
    sessions: "4–6 sessions, 2–4 weeks apart",
    duration: "20–30 minutes",
    downtime: "0–3 days of mild flaking",
    steps: [
      { title: "Assessment & priming", points: ["Skin is assessed and often primed at home for 1–2 weeks.", "Peel type and strength are selected."] },
      { title: "Preparation", points: ["Skin is cleansed and degreased.", "Sensitive areas are protected with barrier cream."] },
      { title: "Peel application", points: ["The solution is applied for a controlled contact time.", "Mild tingling or warmth is normal."] },
      { title: "Neutralise & soothe", points: ["The peel is neutralised and cooled.", "Sunscreen and repair cream are applied."] },
    ],
    benefits: [
      "Visibly lighter pigmentation and acne marks.",
      "Smoother texture and reduced clogged pores.",
      "Controls active acne when salicylic peels are used.",
      "Quick sessions with minimal downtime.",
    ],
    causes: ["Melasma and pigmentation", "Post-acne marks", "Sun tanning", "Uneven skin tone and texture"],
    aftercare: [
      "Strict sun protection with SPF 50 sunscreen — reapply every 3–4 hours.",
      "Do not pick or peel flaking skin.",
      "Pause retinol and scrubs for 5–7 days.",
    ],
    faqs: [
      { q: "Will my skin peel visibly?", a: "Light peels cause mild flaking for 2–3 days at most. Deeper peels are only used when clinically needed and are discussed in advance." },
      { q: "Are peels safe for Indian skin?", a: "Yes, when strength and priming are chosen correctly by a dermatologist — this prevents post-inflammatory pigmentation." },
      { q: "How many peels do I need?", a: "Most pigmentation and acne-mark protocols run 4–6 sessions, 2–4 weeks apart." },
    ],
  },
  {
    slug: "acne-and-acne-scar-treatment",
    name: "Acne & Acne Scar Treatment",
    tagline: "Clear active acne, then rebuild smooth skin texture",
    short:
      "A staged plan that first controls active acne with medical therapy, then treats scars with peels, microneedling and laser resurfacing.",
    overview:
      "Acne management at Healic is done in two phases. Active acne is first controlled with topical and oral medical therapy plus anti-acne peels. Once the skin is calm, scars and marks are treated with microneedling radiofrequency, fractional laser resurfacing, subcision for tethered scars and pigment-targeting peels. Treating scars before acne is controlled leads to relapse, so sequence matters.",
    sessions: "Acne control 8–12 weeks; scar treatment 4–6 sessions",
    duration: "30–45 minutes per session",
    downtime: "1–3 days after resurfacing sessions",
    steps: [
      { title: "Grading & cause work-up", points: ["Acne type and severity are graded.", "Hormonal or PCOS work-up is arranged where relevant."] },
      { title: "Active acne control", points: ["Topical and oral medical therapy.", "Anti-acne peels and comedone extraction."] },
      { title: "Scar resurfacing", points: ["Microneedling RF or fractional laser for boxcar and rolling scars.", "Subcision for tethered scars; TCA CROSS for ice-pick scars."] },
      { title: "Maintenance", points: ["Pigment-control creams and sunscreen.", "Periodic review to prevent relapse."] },
    ],
    benefits: [
      "Addresses the cause, not just the breakout.",
      "Significant improvement in scar depth and texture.",
      "Reduces post-acne dark marks.",
      "Plans customised for oily, sensitive and hormonal acne.",
    ],
    causes: ["Hormonal imbalance including PCOS", "Excess sebum and clogged pores", "Bacterial overgrowth and inflammation", "Picking or squeezing lesions"],
    aftercare: [
      "Follow the prescribed routine consistently — acne relapses without maintenance.",
      "Use a non-comedogenic moisturiser and sunscreen.",
      "Avoid picking or squeezing lesions.",
    ],
    faqs: [
      { q: "Can acne scars be removed completely?", a: "Most patients achieve 50–80% improvement across a full course. Complete erasure is rarely realistic, but texture and shadowing improve markedly." },
      { q: "Should I treat scars while I still have acne?", a: "No. Active acne must be controlled first, otherwise new lesions create new scars." },
      { q: "Is isotretinoin always needed?", a: "No. It is reserved for severe, scarring or treatment-resistant acne and is always monitored." },
    ],
  },
  {
    slug: "prp-hair-treatment",
    name: "PRP Hair Treatment",
    tagline: "Your own growth factors to reduce hair fall and thicken hair",
    short:
      "Platelet-rich plasma prepared from your own blood is injected into the scalp to strengthen follicles and reduce hair fall.",
    overview:
      "PRP (platelet-rich plasma) therapy uses a small sample of your own blood, spun in a centrifuge to concentrate platelets and growth factors, which is then injected into the thinning areas of the scalp. These growth factors improve blood supply to the follicle, prolong the growth phase and reduce shedding. It is most effective in early to moderate pattern hair loss and works well alongside medical therapy.",
    sessions: "4–6 sessions, 4 weeks apart, then maintenance",
    duration: "30–45 minutes",
    downtime: "None — mild scalp tenderness for a day",
    steps: [
      { title: "Assessment", points: ["Pattern and grade of hair loss are examined.", "Blood tests for thyroid, iron, vitamin D and hormones where indicated."] },
      { title: "Blood draw & processing", points: ["A small blood sample is taken.", "It is centrifuged to separate platelet-rich plasma."] },
      { title: "Scalp injection", points: ["Numbing is applied.", "PRP is injected across thinning zones with fine needles."] },
      { title: "Review", points: ["Growth is reviewed at 3 and 6 months with photographs.", "Medical therapy is continued alongside."] },
    ],
    benefits: [
      "Uses your own blood — no foreign substance.",
      "Reduces hair fall within 2–3 sessions for most patients.",
      "Improves hair thickness and scalp density.",
      "No downtime; suitable for men and women.",
    ],
    causes: ["Male and female pattern hair loss", "Telogen effluvium after illness or delivery", "Nutritional deficiency and stress-related shedding", "Thinning after weight loss or PCOS"],
    aftercare: [
      "Do not wash the scalp for 12–24 hours.",
      "Avoid hair colouring and chemical treatments for a week.",
      "Continue prescribed medical therapy and supplements.",
    ],
    faqs: [
      { q: "When will I see results?", a: "Reduced shedding is usually noticed after 2–3 sessions; visible density improves around 4–6 months." },
      { q: "Is PRP painful?", a: "A numbing agent is applied and the injections take only a few minutes. Most patients tolerate it easily." },
      { q: "Will hair fall return if I stop?", a: "PRP slows the process but does not cure genetic hair loss — maintenance sessions once or twice a year are advised." },
    ],
  },
  {
    slug: "anti-ageing-and-skin-rejuvenation",
    name: "Anti-Ageing & Skin Rejuvenation",
    tagline: "Botox, fillers, skin boosters and collagen stimulation",
    short:
      "Non-surgical treatments to soften lines, restore volume and improve skin firmness with natural-looking results.",
    overview:
      "Anti-ageing treatments at Healic combine botulinum toxin for dynamic lines, hyaluronic acid fillers for volume loss and contour, skin boosters for hydration, and collagen-stimulating treatments such as microneedling radiofrequency. Treatment is planned conservatively so the face still moves and looks like you — the goal is a rested, refreshed appearance rather than an altered one.",
    sessions: "Single session; repeat at 6–12 months",
    duration: "20–45 minutes",
    downtime: "0–2 days of mild swelling or bruising",
    steps: [
      { title: "Facial assessment", points: ["Dynamic lines, volume loss and skin quality are mapped.", "A staged plan and realistic outcome are discussed."] },
      { title: "Preparation", points: ["Skin is cleansed and numbing cream applied where needed.", "Injection points are marked."] },
      { title: "Treatment", points: ["Botulinum toxin for forehead, frown and crow's feet.", "Fillers for cheeks, tear trough, chin and lips.", "Skin boosters or microneedling RF for texture and firmness."] },
      { title: "Review", points: ["Botox result reviewed at 2 weeks for fine-tuning.", "Maintenance schedule planned."] },
    ],
    benefits: [
      "Softer lines with natural expression retained.",
      "Restores lost volume and improves contour.",
      "Little to no downtime.",
      "Fully reversible options available with hyaluronic acid fillers.",
    ],
    causes: ["Age-related collagen and volume loss", "Sun damage and smoking", "Repeated facial expression lines", "Rapid weight loss"],
    aftercare: [
      "Stay upright for 4 hours and avoid rubbing the treated area after Botox.",
      "Avoid heavy exercise, steam and alcohol for 24–48 hours.",
      "Apply sunscreen daily and keep skin well moisturised.",
    ],
    faqs: [
      { q: "Will I look unnatural?", a: "No. We use conservative dosing and review at two weeks, so expressions stay natural." },
      { q: "How long do results last?", a: "Botulinum toxin lasts 4–6 months; hyaluronic acid fillers typically 9–18 months depending on the area and product." },
      { q: "Are fillers reversible?", a: "Yes — hyaluronic acid fillers can be dissolved with an enzyme if needed." },
    ],
  },
  {
    slug: "pigmentation-and-melasma-treatment",
    name: "Pigmentation & Melasma Treatment",
    tagline: "Even skin tone with medical creams, peels and laser toning",
    short:
      "A combination protocol of prescription depigmenting agents, peels, laser toning and strict sun protection.",
    overview:
      "Pigmentation problems such as melasma, sun tanning, freckles and post-inflammatory hyperpigmentation need a combination approach. Treatment starts with identifying triggers (sun, hormones, thyroid, cosmetics), followed by prescription depigmenting creams, series of peels, and laser toning where indicated. Sun protection is the single most important part of the plan — without it, pigmentation returns.",
    sessions: "6–8 sessions, 2–4 weeks apart",
    duration: "20–30 minutes",
    downtime: "None to minimal",
    steps: [
      { title: "Trigger identification", points: ["Sun exposure, hormonal factors and cosmetic irritants are reviewed.", "Depth of pigment assessed under Wood's lamp where needed."] },
      { title: "Medical therapy", points: ["Prescription depigmenting creams and antioxidants started.", "Correct sunscreen and application quantity taught."] },
      { title: "Procedures", points: ["Series of medical peels tailored to pigment depth.", "Laser toning sessions for stubborn dermal pigment."] },
      { title: "Maintenance", points: ["Long-term maintenance cream and sunscreen routine.", "Periodic review to prevent relapse."] },
    ],
    benefits: [
      "Gradual, safe lightening without skin damage.",
      "Addresses triggers so results last.",
      "Safe protocols designed for Indian skin tones.",
      "Improves overall clarity and glow.",
    ],
    causes: ["Sun exposure and heat", "Hormonal changes, pregnancy and oral contraceptives", "Thyroid disorders", "Irritant cosmetics and steroid cream misuse"],
    aftercare: [
      "Apply broad-spectrum SPF 50 every 3–4 hours, even indoors near windows.",
      "Never use unprescribed fairness or steroid creams.",
      "Be patient — melasma improves over months, not days.",
    ],
    faqs: [
      { q: "Can melasma be cured permanently?", a: "Melasma is a chronic, relapsing condition. It can be controlled very well, but maintenance therapy and sun protection are lifelong." },
      { q: "Is laser safe for pigmentation?", a: "Laser toning is safe when correctly selected and spaced. Aggressive laser on melasma can worsen it, which is why we combine it with medical therapy." },
      { q: "How soon will I see change?", a: "Most patients see visible lightening in 6–8 weeks of a combined protocol." },
    ],
  },
  {
    slug: "tattoo-removal",
    name: "Laser Tattoo Removal",
    tagline: "Fade or fully clear unwanted tattoos with Q-switched laser",
    short:
      "Q-switched laser breaks tattoo pigment into tiny particles that the body clears naturally over several sessions.",
    overview:
      "Laser tattoo removal uses a Q-switched laser that delivers extremely short, high-energy pulses. These shatter the tattoo ink into microscopic particles which are then cleared by the body's immune system. The number of sessions depends on ink colour, density, depth, tattoo age and your skin type — black and dark blue ink respond fastest, while green, yellow and light blue take longer.",
    sessions: "6–12 sessions, 6–8 weeks apart",
    duration: "10–30 minutes",
    downtime: "3–7 days of scabbing",
    steps: [
      { title: "Assessment", points: ["Ink colours, depth and skin type are assessed.", "Realistic session count and outcome explained."] },
      { title: "Preparation", points: ["Numbing cream applied; protective eyewear worn.", "Area cleaned and cooled."] },
      { title: "Laser session", points: ["Q-switched pulses are passed over the tattoo.", "Pigment shatters and skin frosts briefly."] },
      { title: "Healing", points: ["Antibiotic ointment and dressing applied.", "Body clears pigment over the following weeks."] },
    ],
    benefits: [
      "Non-surgical with no cutting or stitches.",
      "Selective — targets ink, spares surrounding skin.",
      "Works on both amateur and professional tattoos.",
      "Can be used to fade a tattoo for a cover-up.",
    ],
    causes: ["Regret over an old or amateur tattoo", "Professional or workplace requirements", "Preparation for a cover-up design", "Allergic or inflammatory reaction to ink"],
    aftercare: [
      "Keep the area dry and covered for 24–48 hours.",
      "Do not scratch or pick scabs.",
      "Apply the prescribed ointment and use SPF 50 once healed.",
    ],
    faqs: [
      { q: "Will the tattoo disappear completely?", a: "Black ink usually clears very well. Multi-coloured and dense professional tattoos may leave a faint shadow." },
      { q: "Does it hurt?", a: "It feels like a rubber band snap. Numbing cream and cooling make it comfortable." },
      { q: "Will it scar?", a: "Scarring is uncommon when aftercare is followed and scabs are not picked." },
    ],
  },
];

export function findAestheticTreatment(slug: string) {
  return aestheticTreatments.find((t) => t.slug === slug);
}

export const aestheticsPaths = [
  "/aesthetics",
  ...aestheticTreatments.map((t) => `/aesthetics/${t.slug}`),
];
